import { useNavigate } from "react-router";
import { Fragment, useEffect, useState } from "react";
import {
  Boxes,
  ClipboardList,
  Moon,
  PackagePlus,
  PanelLeft,
  Search,
  Sun,
  Truck,
  UserRound,
} from "lucide-react";
import { formatINR } from "@milqit/ui";

import { useTheme } from "@/components/providers/theme-provider";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { Kbd } from "@/components/ui/kbd";
import { useSidebar } from "@/components/ui/sidebar";
import { Can, useAbility } from "@/lib/auth/ability";
import { visibleNavGroups } from "@/lib/navigation";
import { useRecordSearch } from "@/lib/queries";
import { ProductThumb } from "@/components/product-thumb";

/** Wait this long after typing before searching records. */
const SEARCH_DELAY_MS = 250;

/** ⌘K palette: jump to pages, records (products, orders, customers) and run quick actions. */
export function CommandMenu() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { resolvedTheme, setTheme } = useTheme();
  const { toggleSidebar } = useSidebar();
  const ability = useAbility();
  // Records are searched on the server once typing pauses.
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  useEffect(() => {
    const timer = window.setTimeout(() => setTerm(search.trim()), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [search]);
  const { products, orders, customers } = useRecordSearch(term);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const run = (action: () => void) => {
    setOpen(false);
    action();
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="bg-muted/40 text-muted-foreground h-8 w-full justify-start gap-2 px-2.5 font-normal shadow-none sm:w-56 lg:w-72"
      >
        <Search />
        <span className="truncate">Search orders, products…</span>
        <Kbd className="ml-auto hidden sm:inline-flex">⌘K</Kbd>
      </Button>
      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        title="Search Milqit admin"
        description="Jump to a page, order, product or customer"
      >
        <Command>
          <CommandInput
            placeholder="Type an order number, product, customer or page…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup heading="Quick actions">
              <Can I="create" a="Product">
                <CommandItem onSelect={() => run(() => navigate("/products/new"))}>
                  <PackagePlus /> New product
                </CommandItem>
              </Can>
              <Can I="update" a="Inventory">
                <CommandItem onSelect={() => run(() => navigate("/inventory?status=low_stock"))}>
                  <Boxes /> Restock low items
                </CommandItem>
              </Can>
              <Can I="update" a="Delivery">
                <CommandItem onSelect={() => run(() => navigate("/deliveries?agentId=none"))}>
                  <Truck /> Assign tomorrow’s drops
                </CommandItem>
              </Can>
              <CommandItem onSelect={() => run(toggleSidebar)}>
                <PanelLeft /> Toggle sidebar
                <CommandShortcut>⌘B</CommandShortcut>
              </CommandItem>
              <CommandItem
                onSelect={() => run(() => setTheme(resolvedTheme === "dark" ? "light" : "dark"))}
              >
                {resolvedTheme === "dark" ? <Sun /> : <Moon />}
                Switch to {resolvedTheme === "dark" ? "light" : "dark"} mode
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            {visibleNavGroups(ability).map((group) => (
              <Fragment key={group.label}>
                <CommandGroup heading={group.label}>
                  {group.items.map((item) => (
                    <CommandItem
                      key={item.url}
                      value={`${item.title} ${item.keywords?.join(" ") ?? ""}`}
                      onSelect={() => run(() => navigate(item.url))}
                    >
                      <item.icon /> {item.title}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Fragment>
            ))}
            {orders.length ? (
              <CommandGroup heading="Orders">
                {orders.map((order) => (
                  <CommandItem
                    key={order.id}
                    value={`order ${order.number} ${order.customerName} ${order.customerPhone}`}
                    onSelect={() => run(() => navigate(`/orders/${order.id}`))}
                  >
                    <ClipboardList />
                    <span className="font-mono text-xs">{order.number}</span>
                    <span className="text-muted-foreground truncate">{order.customerName}</span>
                    <span className="text-muted-foreground ml-auto text-xs tabular-nums">
                      {formatINR(order.total)}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
            {products.length ? (
              <CommandGroup heading="Products">
                {products.map((product) => (
                  <CommandItem
                    key={product.id}
                    value={`product ${product.name} ${product.sku} ${product.unit}`}
                    onSelect={() => run(() => navigate(`/products/${product.id}`))}
                  >
                    <ProductThumb
                      src={product.image.src}
                      alt=""
                      tone={product.tone}
                      kind={product.image.kind}
                      size="xs"
                      className="size-5 rounded"
                    />
                    <span className="truncate">{product.name}</span>
                    <span className="text-muted-foreground text-xs">{product.unit}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
            {customers.length ? (
              <CommandGroup heading="Customers">
                {customers.map((customer) => (
                  <CommandItem
                    key={customer.id}
                    value={`customer ${customer.name} ${customer.phone}`}
                    onSelect={() => run(() => navigate(`/customers/${customer.id}`))}
                  >
                    <UserRound />
                    <span className="truncate">{customer.name}</span>
                    <span className="text-muted-foreground ml-auto text-xs">{customer.city}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  );
}
